import { useState, useCallback, useRef } from 'react';
import Card from './ui/Card';
import { DocumentType, getVisibleDocumentTypes, calculateCompletionPercentage } from '../config/documentTypes';
import { guessDocumentType } from '../config/documentTypes';

interface UploadedDocument {
  id: string;
  documentTypeId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  s3Key?: string;
  status: 'pending' | 'uploading' | 'complete' | 'failed';
  progress: number;
  error?: string;
  retryCount: number;
  uploadedAt?: string;
  file?: File;
}

interface DocumentFolderProps {
  tenure: 'freehold' | 'leasehold';
  alterationsMade: boolean;
  documents: UploadedDocument[];
  markedUnavailable: Set<string>;
  isPaid?: boolean;
  onFileSelect: (file: File, documentTypeId: string) => void;
  onMarkUnavailable: (documentTypeId: string) => void;
  onRemoveFile: (documentId: string) => void;
  onRetryUpload: (documentId: string) => void;
  onDownloadDocument?: (documentId: string) => void;
}

type AssignPrompt = { file: File; suggestedTypeId: string | null };

function StatusIcon({ status, isUnavailable }: { status?: string; isUnavailable?: boolean }) {
  if (isUnavailable) return <span className="text-slate-400" aria-label="Marked unavailable">⊘</span>;
  if (status === 'complete') return <span className="text-green-600" aria-label="Uploaded">✅</span>;
  if (status === 'uploading') return <span className="text-blue-600" aria-label="Uploading">⏳</span>;
  if (status === 'failed') return <span className="text-red-600" aria-label="Failed">❌</span>;
  return <span className="text-slate-300" aria-label="Missing">⬜</span>;
}

export default function DocumentFolder({
  tenure,
  alterationsMade,
  documents,
  markedUnavailable,
  isPaid = false,
  onFileSelect,
  onMarkUnavailable,
  onRemoveFile,
  onRetryUpload,
  onDownloadDocument,
}: DocumentFolderProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [assignPrompt, setAssignPrompt] = useState<AssignPrompt | null>(null);
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addDocTypeId, setAddDocTypeId] = useState<string>('');

  const visibleDocs = getVisibleDocumentTypes(tenure, alterationsMade);
  const requiredDocs = visibleDocs.filter(dt => dt.required);
  const optionalDocs = visibleDocs.filter(dt => !dt.required);

  const uploadedDocTypeIds = new Set(
    documents.filter(d => d.status === 'complete').map(d => d.documentTypeId)
  );
  const completionPercentage = calculateCompletionPercentage(visibleDocs, uploadedDocTypeIds, markedUnavailable);

  const getDocForType = (docType: DocumentType) =>
    documents.filter(d => d.documentTypeId === docType.id);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;

    const guessedId = guessDocumentType(file.name);
    if (guessedId) {
      setAssignPrompt({ file, suggestedTypeId: guessedId });
      setSelectedTypeId(guessedId);
    } else {
      setAssignPrompt({ file, suggestedTypeId: null });
      setSelectedTypeId('');
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const confirmAssignment = () => {
    if (!assignPrompt || !selectedTypeId) return;
    onFileSelect(assignPrompt.file, selectedTypeId);
    setAssignPrompt(null);
    setSelectedTypeId('');
  };

  const cancelAssignment = () => {
    setAssignPrompt(null);
    setSelectedTypeId('');
  };

  const handleAddDocument = () => {
    if (!addDocTypeId) return;
    fileInputRef.current?.click();
  };

  const handleAddFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && addDocTypeId) {
      onFileSelect(file, addDocTypeId);
      setAddDocTypeId('');
    }
    e.target.value = '';
  };

  const renderDocRow = (docType: DocumentType) => {
    const docsForType = getDocForType(docType);
    const isUnavailable = markedUnavailable.has(docType.id);
    const latestDoc = docsForType[docsForType.length - 1];

    return (
      <div
        key={docType.id}
        className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between py-3 px-4 border-b border-slate-100 last:border-b-0"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <StatusIcon
            status={latestDoc?.status}
            isUnavailable={isUnavailable}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-950 truncate">{docType.name}</p>
            {latestDoc?.status === 'complete' && (
              <p className="text-xs text-slate-600 truncate">{latestDoc.fileName}</p>
            )}
            {isUnavailable && (
              <p className="text-xs text-yellow-700">Marked as unavailable</p>
            )}
            {latestDoc?.status === 'uploading' && (
              <p className="text-xs text-blue-600">Uploading...</p>
            )}
            {latestDoc?.status === 'failed' && (
              <p className="text-xs text-red-700">{latestDoc.error || 'Upload failed'}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0 ml-8 sm:ml-0">
          {isPaid && latestDoc?.status === 'complete' && onDownloadDocument && (
            <button
              onClick={() => onDownloadDocument(latestDoc.id)}
              className="text-xs text-emerald-700 hover:text-emerald-800 underline"
            >
              Download
            </button>
          )}
          {!isPaid && !isUnavailable && (
            <>
              {latestDoc?.status === 'failed' && latestDoc.retryCount < 3 && (
                <button
                  onClick={() => onRetryUpload(latestDoc.id)}
                  className="text-xs text-blue-700 hover:underline"
                >
                  Retry
                </button>
              )}
              {latestDoc?.status === 'complete' && (
                <button
                  onClick={() => onRemoveFile(latestDoc.id)}
                  className="text-xs text-slate-600 hover:text-red-600"
                  aria-label={`Remove ${docType.name}`}
                >
                  Remove
                </button>
              )}
              {(!latestDoc || latestDoc.status === 'failed') && (
                <label className="text-xs text-emerald-700 hover:text-emerald-800 underline cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.tiff"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        onFileSelect(file, docType.id);
                        e.target.value = '';
                      }
                    }}
                  />
                  Upload
                </label>
              )}
              {docType.required && !latestDoc && !isUnavailable && (
                <button
                  onClick={() => onMarkUnavailable(docType.id)}
                  className="text-xs text-slate-600 hover:underline"
                >
                  Don't have it
                </button>
              )}
              {isUnavailable && (
                <button
                  onClick={() => onMarkUnavailable(docType.id)}
                  className="text-xs text-blue-700 hover:underline"
                >
                  I have it now
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Completion summary */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-950">Document folder</h3>
          <span className="text-2xl font-semibold text-slate-950">{completionPercentage}%</span>
        </div>
        <div
          className="w-full bg-slate-200 rounded-full h-3"
          role="progressbar"
          aria-valuenow={completionPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Document completion progress"
        >
          <div
            className={`h-3 rounded-full transition-all ${
              completionPercentage === 100 ? 'bg-green-600' : completionPercentage >= 50 ? 'bg-blue-600' : 'bg-yellow-600'
            }`}
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
        <p className="text-sm text-slate-600 mt-2">
          {requiredDocs.filter(dt => uploadedDocTypeIds.has(dt.id) || markedUnavailable.has(dt.id)).length} of {requiredDocs.length} required documents provided
        </p>
      </Card>

      {/* Drop zone (only when not paid) */}
      {!isPaid && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
            isDragOver ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-slate-50'
          }`}
        >
          <p className="text-sm text-slate-700">
            {isDragOver ? 'Drop file here' : 'Drag and drop a file here to upload'}
          </p>
          <p className="text-xs text-slate-600 mt-1">PDF, JPG, PNG, or TIFF. Maximum 10MB.</p>
        </div>
      )}

      {/* Add document picker */}
      {!isPaid && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label htmlFor="add-doc-type" className="text-sm font-semibold text-slate-950">Add document</label>
            <select
              id="add-doc-type"
              value={addDocTypeId}
              onChange={(e) => setAddDocTypeId(e.target.value)}
              className="mt-1 w-full rounded-lg border-slate-300 bg-white px-3 py-3 text-base text-slate-950"
            >
              <option value="">Select document type...</option>
              {visibleDocs.map(dt => (
                <option key={dt.id} value={dt.id}>{dt.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleAddDocument}
            disabled={!addDocTypeId}
            className="btn-secondary shrink-0"
          >
            Choose file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.tiff"
            className="hidden"
            onChange={handleAddFileSelected}
          />
        </div>
      )}

      {/* Required documents */}
      {requiredDocs.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-slate-950 mb-2">Required documents</h3>
          <div>{requiredDocs.map(renderDocRow)}</div>
        </Card>
      )}

      {/* Optional documents */}
      {optionalDocs.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-slate-950 mb-2">Optional documents</h3>
          <div>{optionalDocs.map(renderDocRow)}</div>
        </Card>
      )}

      {/* Assignment modal for drag-and-drop */}
      {assignPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="max-w-md mx-4" role="dialog" aria-modal="true" aria-labelledby="assign-doc-title">
            <h3 id="assign-doc-title" className="text-lg font-semibold text-slate-950">Assign document type</h3>
            <p className="text-sm text-slate-600 mt-2">
              File: <span className="font-medium text-slate-900">{assignPrompt.file.name}</span>
            </p>
            {assignPrompt.suggestedTypeId && (
              <p className="text-sm text-emerald-700 mt-1">
                Suggested: {visibleDocs.find(d => d.id === assignPrompt.suggestedTypeId)?.name}
              </p>
            )}
            <div className="mt-4">
              <label htmlFor="assign-type-select" className="text-sm font-semibold text-slate-950">Document type</label>
              <select
                id="assign-type-select"
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(e.target.value)}
                className="mt-1 w-full rounded-lg border-slate-300 bg-white px-3 py-3 text-base text-slate-950"
              >
                <option value="">Select type...</option>
                {visibleDocs.map(dt => (
                  <option key={dt.id} value={dt.id}>{dt.name}</option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={cancelAssignment} className="btn-secondary">Cancel</button>
              <button onClick={confirmAssignment} disabled={!selectedTypeId} className="btn-primary">Upload</button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
